import { Component } from "react";
import { Waffles } from "@app/shared/waffles/Waffles";
import { Accordion } from "@app/shared/Accordion";
import { AccordionContainer } from "@app/shared/AccordionContainer";
import { QESButton } from "@app/shared/qes-standards/Button";
import Roles from "@app/user/roles";
import { UserContextProps, withUserContext } from "@app/user/UserContext";
import { printNumberAsString, ColumnConfig, SortDirections, numberSort } from "@app/shared/waffles";
import { AccessCriteriaGranularityEnum, TemplateAccessCriteria } from "@app/templates/templateModels";
import TemplateService from "@app/templates/templateService";

interface AccessCriteriaPanelProps {
  accessCriteriaSpecificationGranularity: AccessCriteriaGranularityEnum;
  className?: string;
  isTelehealthSpecialtyGroup: boolean;
  templateCollectionId: number;
  templateId: string;
  specialtyGroupCode: string;
  specialtyGroupName: string;
}

interface AccessCriteriaPanelState {
  isOpen: boolean;
  loadStatus: LoadStatus;
  offset: number;
  sortedRecords: TemplateAccessCriteria[];
  totalCount: number;
}

interface AccessCriteriaData {
  key: string;
  specialtyGroupCode: string;
  specialtyGroupName: string;
  stateName: string;
  geographyDescription: string;
  countyName: string;
  passPercentage: number;
  minimumProviderCount: number;
  maximumDistanceAllowed: string;
  maximumTimeAllowed: string;
  isAccessCustomization: string;
  conAccessCreditPercentage: string;
  telehealthAccessCreditPercentage: string;
  cmsApplicationCreditPercentage: string;
}

type LoadStatus = "empty" | "error" | "loaded" | "loading" | "partialLoading" | "shouldLoad";

type AccessCriteriaProps = AccessCriteriaPanelProps & UserContextProps;

class AccessCriteriaPanel extends Component<AccessCriteriaProps,
  AccessCriteriaPanelState> {
  private readonly batchCount: number = 4000;
  private readonly templateService = new TemplateService();

  constructor(props: AccessCriteriaProps) {
    super(props);

    this.state = {
      isOpen: false,
      loadStatus: "shouldLoad",
      offset: 0,
      sortedRecords: [],
      totalCount: 0,
    };
  }


  public render() {
    const initialLoading = !this.state || (this.state.loadStatus === "loading" || this.state.loadStatus === "shouldLoad");
    const shouldRenderResults = this.state && (this.state.loadStatus === "loaded" || this.state.loadStatus === "partialLoading");
    const noResultsOrError = this.state && (this.state.loadStatus === "empty" || this.state.loadStatus === "error");
    const shouldShowBatchCount = !!this.state && (this.state.isOpen && (this.state.loadStatus === "loaded" || this.state.loadStatus === "partialLoading"));
    const title = `Access Criteria ${shouldShowBatchCount ? ` (${this.state.sortedRecords.length} of ${this.state.totalCount})` : ""}`;

    return (
      <AccordionContainer className={this.props.className}>
        <Accordion
          defaultOpen={false}
          title={title}
          onPanelWillOpen={initialLoading ? async () => await this.buildAccessCriteria() : null}
          onPanelDidOpen={this.handleOnOpen}
          onPanelDidClose={this.handleOnClose}
          testId="access-criteria-panel__access-criteria-accordion"
        >
          {(initialLoading || null) && (<h3 className="text-muted"><i className="fa fa-spinner fa-spin" />&nbsp;Loading access criteria...</h3>)}
          {(shouldRenderResults || null) && (this.renderAccessCriteriaTable())}
          {(shouldRenderResults || null) && (this.renderLoadMoreControls())}
          {(noResultsOrError || null) && (<h3 className="text-muted">No access criteria found.</h3>)}
        </Accordion>
      </AccordionContainer>
    );
  }

  private handleOnClose = () => {
    this.setState({ isOpen: false });
  }

  private handleOnOpen = () => {
    this.setState({ isOpen: true });
  }

  private buildAccessCriteria = async () => {
    try {
      const nextLoadStatus: LoadStatus = this.state.loadStatus === "shouldLoad"
        ? "loading"
        : (this.state.loadStatus === "loaded" && this.state.offset < this.state.totalCount)
          ? "partialLoading"
          : null;

      if (nextLoadStatus) {
        this.setState((prevState) => ({
          ...prevState,
          loadStatus: nextLoadStatus,
        }));

        const accessCriteriaResult = await this.templateService.getTemplateSpecialtyGroupAccessCriteria(
          this.props.templateCollectionId,
          this.props.templateId,
          this.props.specialtyGroupCode,
          this.props.isTelehealthSpecialtyGroup,
          this.state.offset,
          this.batchCount,
        );

        let loadStatus: LoadStatus = "error";
        let records: TemplateAccessCriteria[] = [];

        if (accessCriteriaResult !== null && accessCriteriaResult.criteria !== null) {
          if (accessCriteriaResult.criteria.length > 0) {
            loadStatus = "loaded";
            records = [...this.state.sortedRecords, ...accessCriteriaResult.criteria];
          } else {
            loadStatus = "empty";
          }
        }

        this.setState((prevState) => ({
          ...prevState,
          ...accessCriteriaResult,
          loadStatus,
          offset: prevState.offset + accessCriteriaResult.batchCount,
          sortedRecords: records,
        }));
      }
    } catch (ex) {
      this.setState((prevState): AccessCriteriaPanelState => ({
        ...prevState,
        loadStatus: "error",
        sortedRecords: [],
      }));
    }
  }

  private renderLoadMoreControls = () => {
    const haveMoreToLoad = this.state.offset < this.state.totalCount;
    const partialLoading = haveMoreToLoad && this.state.loadStatus === "partialLoading";

    return (haveMoreToLoad || null) && (
      <div className="text-center">
        <h3 className="mtb2-imp text-muted"><i className={`fa fa-spinner fa-spin ${partialLoading ? "visible" : "invisible"}`} /></h3>
        <QESButton qesTestId="access-criteria-panel_load-more" onClick={async () => await this.buildAccessCriteria()} disabled={this.state.loadStatus !== "loaded"}>
          Load More
        </QESButton>
      </div>
    );
  }

  private renderAccessCriteriaTable = () => {
    const data: AccessCriteriaData[] = this.state.sortedRecords.map((acd) => (
      {
        cmsApplicationCreditPercentage: acd.cmsApplicationCreditPercentage > 0 ? acd.cmsApplicationCreditPercentage.toString() : "---",
        conAccessCreditPercentage: acd.conAccessCreditPercentage > 0 ? acd.conAccessCreditPercentage.toString() : "---",
        countyName: acd.countyName,
        geographyDescription: acd.geographyDescription,
        isAccessCustomization: acd.isAccessCustomization ? "Y" : "N",
        key: acd.geographyId.toString(),
        maximumDistanceAllowed: printNumberAsString(acd.maximumDistanceAllowed, "---"),
        maximumTimeAllowed: printNumberAsString(acd.maximumTimeAllowed, "---"),
        minimumProviderCount: acd.minimumProviderCount,
        passPercentage: acd.passPercentage,
        specialtyGroupCode: this.props.specialtyGroupCode,
        specialtyGroupName: this.props.specialtyGroupName,
        stateName: acd.stateName,
        telehealthAccessCreditPercentage: acd.telehealthAccessCreditPercentage > 0 ? acd.telehealthAccessCreditPercentage.toString() : "---"
      }
    ));
    const cmsHasData = this.state.sortedRecords.filter(acd => acd.cmsApplicationCreditPercentage > 0).length > 0;
    return (
      <>
        <Waffles<AccessCriteriaData>
          columns={this.getColumnConfig(cmsHasData)}
          customSort={this.customSort}
          data={data}
          fullWidth={true}
          testId="access-criteria"
        />
      </>
    );
  }

  private getColumnConfig = (cmsHasData: boolean) => {
    let displayGeography = true;
    let displayCountyName = false;
    let displayStateName = false;
    let geographyTypeDescription: string = null;

    switch (this.props.accessCriteriaSpecificationGranularity) {
      case AccessCriteriaGranularityEnum.CountySSA:
        geographyTypeDescription = "County SSA";
        displayCountyName = true;
        displayStateName = true;
        break;
      case AccessCriteriaGranularityEnum.ZipCode:
      case AccessCriteriaGranularityEnum.ZipClass:
        geographyTypeDescription = "Zip Code";
        break;
      case AccessCriteriaGranularityEnum.CountyClass:
        geographyTypeDescription = "County";
        break;
      default:
        displayGeography = false;
        break;
    }

    const displayAccessCustomization = this.state.sortedRecords.some(r => r.isAccessCustomization === true);
    const displayConPercent = this.state.sortedRecords.some(r => r.conAccessCreditPercentage > 0);
    const displayTelehealthPercent = this.state.sortedRecords.some(r => r.telehealthAccessCreditPercentage > 0);
    const displayCmsApplicationCreditPercetage = this.props.user.roles.includes(Roles.CMS_APPLICATION_CREDIT_VIEWER) && cmsHasData

    const columnConfig: ColumnConfig = {
      cmsApplicationCreditPercentage: {
        displayText: "CMS Application Credit %",
        hidden: !displayCmsApplicationCreditPercetage,
        sortOrder: 13,
        sticky: true,
        testId: "CMS-Application-Credit-Percentage"
      },
      conAccessCreditPercentage: {
        displayText: "CON %",
        hidden: !displayConPercent,
        sortOrder: 11,
        sticky: true,
        testId: "con-access-credit-percentage"
      },
      countyName: {
        displayText: "County",
        hidden: !displayCountyName,
        sortOrder: 5,
        sticky: true,
        testId: "county-name"
      },
      geographyDescription: {
        displayText: geographyTypeDescription,
        hidden: !displayGeography,
        sortOrder: 4,
        sticky: true,
        testId: "geography-description"
      },
      isAccessCustomization: {
        displayText: "Customization",
        hidden: !displayAccessCustomization,
        sortOrder: 10,
        sticky: true,
        testId: "is-access-customization"
      },
      maximumDistanceAllowed: {
        displayText: "Max Distance",
        sortOrder: 8,
        sticky: true,
        testId: "maximum-distance-allowed"
      },
      maximumTimeAllowed: {
        displayText: "Max Time",
        sortOrder: 9,
        sticky: true,
        testId: "maximum-time-allowed"
      },
      minimumProviderCount: {
        displayText: "Min Provider Count",
        sortOrder: 7,
        sticky: true,
        testId: "minimum-provider-count"
      },
      passPercentage: {
        displayText: "Pass %",
        sortOrder: 6,
        sticky: true,
        testId: "pass-percentage"
      },
      specialtyGroupCode: {
        displayText: "Code",
        noSort: true,
        sortOrder: 1,
        sticky: true,
        testId: "specialty-group-code"
      },
      specialtyGroupName: {
        displayText: "Name",
        noSort: true,
        sortOrder: 2,
        sticky: true,
        testId: "specialty-group-name"
      },
      stateName: {
        displayText: "State",
        hidden: !displayStateName,
        sortOrder: 3,
        sticky: true,
        testId: "state-name"
      },
      telehealthAccessCreditPercentage: {
        displayText: "Telehealth %",
        hidden: !displayTelehealthPercent,
        sortOrder: 12,
        sticky: true,
        testId: "telehealth-access-credit-percentage"
      }

    }

    return columnConfig;
  }

  private customSort = (records: AccessCriteriaData[], column: keyof AccessCriteriaData, sortDirection: SortDirections) => {
    const multiplier = sortDirection === SortDirections.Asc ? 1 : -1;

    switch (column) {
      case "conAccessCreditPercentage":
      case "maximumDistanceAllowed":
      case "maximumTimeAllowed":
      case "telehealthAccessCreditPercentage":
      case "cmsApplicationCreditPercentage":
        return numberSort<AccessCriteriaData>(records, column, multiplier);
      case "minimumProviderCount":
      case "passPercentage":
        return numberSort(records, column, multiplier);
      case "countyName":
      case "geographyDescription":
      case "isAccessCustomization":
      case "stateName":
        return records.sort((a, b) => (a[column] > b[column] ? 1 : -1) * multiplier);
      default:
        return records;
    }
  }
}

const WrappedAccessCriteriaPanel = withUserContext(AccessCriteriaPanel);
export default WrappedAccessCriteriaPanel;